import { notifications } from "@mantine/notifications";
import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import { getRptSupplierOrders } from "api/supplier.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
} from "common/constants";
import { pickFields, toCsv } from "common/csv";
import {
  formatDate,
  formatNumberAsCurrencyString,
  formatNZD,
} from "common/utils";
import PreviewHeader from "components/header/PreviewHeader";
import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import type {
  ISupplierOrderPreviewProps,
  RptSupplierOrderDetail,
  RptSupplierOrders,
} from "types/model.types";

export const AdminSupplierCostingPreview: React.FC<
  ISupplierOrderPreviewProps
> = ({ id, previewType = "costing", onClose }) => {
  // 1. Receive the trigger from MainPrintPage
  const { csvRequestId } = useOutletContext<{ csvRequestId: number }>();
  const [initialData, setInitialData] = useState<RptSupplierOrders | null>(
    null,
  );

  // 2. React to the trigger
  useEffect(() => {
    if (!csvRequestId) return; // ignore initial mount

    if (!initialData) {
      notifications.show({
        title: "Supplier Download",
        message: "No data available for CSV download or line items empty.",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    }

    const summaryRow = {
      Date: formatDate(initialData.summary.createdOn),
      Supplier: initialData.summary.supplierName,
      NZRate: initialData.summary.exchange,
      Commission: initialData.summary.commission,
      Sell1: initialData.summary.sell1,
      Sell2: initialData.summary.sell2,
      Freight: initialData.summary.freight,
    };

    // only two types: costing or prices.  Other types are used in AdminSupplierORderPreview.tsx
    const CSV_FIELDS: (keyof RptSupplierOrderDetail)[] =
      previewType === "costing"
        ? [
            "productId",
            "productCode",
            "productDescription",
            "quan",
            "units",
            "nzd",
            "commission",
            "duty",
            "freight",
            "unitCost",
            "sell1",
            "sell2",
            "change",
          ]
        : [
            "productId",
            "productCode",
            "productDescription",
            "quan",
            "units",
            "change",
            "sell1",
            "oldSell1",
            "sell2",
            "oldSell2",
          ];
    const detailRows = pickFields(initialData?.entries ?? [], CSV_FIELDS);
    // Generate CSV from the data inside THIS print page
    const summaryCsv = toCsv([summaryRow]);
    const csv = toCsv(detailRows ?? []);

    const blob = new Blob([`${summaryCsv}\n\n${csv}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `supplier-order-${previewType}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  }, [csvRequestId]);

  useEffect(() => {
    if (!id) return;

    let mounted = true;
    const fetchData = async () => {
      try {
        const response = await safeApiCall(getRptSupplierOrders(id));
        if (mounted) setInitialData(response ?? null);
      } catch (error) {
        logError(error);
        notifications.show({
          message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });
      }
    };
    fetchData();

    return () => {
      mounted = false;
    };
  }, [id]);

  return (
    <>
      <PreviewHeader hideAddress={true} />
      <div className="sub-title">
        Supplier Order Number: {initialData?.summary.id}
      </div>
      <div className="spacer padTop padBottom" />
      <table className="print-table">
        <tbody>
          <tr>
            <td>Date:</td>
            <td>
              {formatDate(
                initialData?.summary.createdOn ?? new Date().toDateString(),
              )}
            </td>
            <td>Supplier:</td>
            <td>{initialData?.summary.supplierName ?? "unknown"}</td>
          </tr>
          <tr>
            <td>NZ Rate:</td>
            <td>
              {formatNumberAsCurrencyString(initialData?.summary.exchange ?? 0)}
            </td>
            <td>Comm (%):</td>
            <td>
              {formatNumberAsCurrencyString(
                initialData?.summary.commission ?? 0,
              )}
            </td>
          </tr>
          <tr>
            <td>Sell #1:</td>
            <td>
              {formatNumberAsCurrencyString(initialData?.summary.sell1 ?? 0)}
            </td>
            <td>Sell #2:</td>
            <td>
              {formatNumberAsCurrencyString(initialData?.summary.sell2 ?? 0)}
            </td>
          </tr>
          <tr>
            <td>Freight:</td>
            <td>{formatNZD(initialData?.summary.freight ?? 0)}</td>
            <td colSpan={2} />
          </tr>
        </tbody>
      </table>
      <div className="spacer padTop padBottom" />
      {/* Render logic for orderDetails array here */}
      <table className="print-table details-table">
        <thead>
          <tr>
            <th className="nowrap">Product</th>
            <th className="description xl">Description</th>
            <th className="number">Quan</th>
            <th className="number">Units</th>
            {previewType === "prices" && <th className="money">Change</th>}
            {previewType !== "prices" && (
              <>
                <th className="money">NZ ($)</th>
                <th className="money">Duty</th>
                <th className="money">Freight</th>
                <th className="money">Unit Cost</th>
              </>
            )}
            {previewType !== "prices" && (
              <>
                <th className="money">Sell #1</th>
                <th className="money">Sell #2</th>
              </>
            )}
            {previewType === "prices" && (
              <>
                <th className="money">Sell #1</th>
                <th className="money"></th>
                <th className="money">Sell #2</th>
                <th className="money"></th>
              </>
            )}
            {previewType !== "prices" && <th className="money">Change</th>}
          </tr>
        </thead>
        <tbody>
          {initialData?.entries.map((item, idx) => (
            <tr key={`${item.id}-${idx}`} className="vat">
              <td className="nowrap">{`${item.productId}${item.productCode}`}</td>
              <td className="description xl">{item.productDescription}</td>
              <td className="number">{item.quan}</td>
              <td className="number">{item.units}</td>
              {previewType === "prices" && (
                <td className="money">{formatNZD(item.change)}</td>
              )}
              {previewType !== "prices" && (
                <>
                  <td className="money">{formatNZD(item.nzd)}</td>
                  <td className="money">{formatNZD(item.duty)}</td>
                  <td className="money">{formatNZD(item.freight)}</td>
                  <td className="money">{formatNZD(item.unitCost)}</td>
                </>
              )}
              {previewType !== "prices" && (
                <>
                  <td className="money">{formatNZD(item.sell1)}</td>

                  <td className="money">{formatNZD(item.sell2)}</td>
                </>
              )}
              {previewType === "prices" && (
                <>
                  <td className="money">{formatNZD(item.oldSell1)}</td>
                  <td className="money money-bold">{formatNZD(item.sell1)}</td>
                  <td className="money">{formatNZD(item.oldSell2)}</td>
                  <td className="money money-bold">{formatNZD(item.sell2)}</td>
                </>
              )}
              {previewType !== "prices" && (
                <td className="money">{formatNZD(item.change)}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

export default AdminSupplierCostingPreview;
